import React, { useState, useEffect } from 'react';
import { Eye, Sparkles, Palette, Calendar, Download } from 'lucide-react';
import GraphicDesignCreativeSkills from '../GraphicDesignCreativeSkills/GraphicDesignCreativeSkills';
import Calendar2026 from '../Calendar2026/Calendar2026';
import GraphicDesignContact from '../GraphicDesignContact/GraphicDesignContact';
import styles from './GraphicDesignContent.module.css';

const GraphicDesignPortfolio = ({ onModeChange }) => {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [hoveredButton, setHoveredButton] = useState(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePos({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const portfolio = [
    { id: 1, title: 'Character Design', category: 'Character Design', route: '/animatedgiftcard', color: '#ec4899', hint: '🎭 Click to see magical characters!' },
    { id: 2, title: 'Logo Creation', category: 'Logo Creation', route: '/logo-creation', color: '#a855f7', hint: '✨ Click to create logo magic!' },
    { id: 3, title: 'Painting Portfolio', category: 'Painting Portfolio', route: '/painting-portfolio', color: '#3b82f6', hint: '🎨 Click for artistic wizardry!' },
    { id: 4, title: 'Comic Script', category: 'Comic Script', route: '/comic-card', color: '#f97316', hint: '💥 Click to unleash comic power!' },
    //{ id: 5, title: 'Day in My Life Drawing', category: 'Day in My Life Drawing', route: '/coming-soon', color: '#10b981', hint: '📸 Click to see daily magic!' },
    { id: 6, title: 'Future', category: 'Future', route: '/future', color: '#eab308', hint: '🚀 Click to teleport to future!' },
    { id: 7, title: 'Product Label Design', category: 'Product Label Design', route: '/product-label', color: '#dc2626', hint: '🛍️ Click for design sorcery!' },
    { id: 8, title: 'Merchandise Design', category: 'Merchandise Design', route: '/merchandise-design', color: '#50f344', hint: '👕 Click for merch magic!' },
    { id: 9, title: 'Book Cover Design', category: 'Book Cover Design', route: '/book-covers', color: '#059669', hint: '📚 Click for bookish magic!' },
    { id: 10, title: 'Book Illustration', category: 'Book Illustration', route: '/book-illustration', color: '#d97706', hint: '✨ Click for illustrated wonders!' },
    { id: 11, title: 'Illustration Art', category: 'Illustration Art', route: '/illustrations', color: '#be185d', hint: '🎨 Click to illustrate magic!' },
    { id: 12, title: 'Creative Space', category: 'Procreate Creative Space', route: '/procreate-space', color: '#0ea5e9', hint: '🌌 Click to enter creative universe!' },
    { id: 13, title: 'Malayalam Movie Posters', category: 'Movie Posters 2025', route: '/malayalam-movie-posters', color: '#f59e0b', hint: '🎬 Click for Malayalam cinema magic!' },
    //{ id: 14, title: 'Storyboarding', category: 'Storyboard', route: '/storyboard', color: '#8b5cf6', hint: '⚡ Click to storyboard magic!' },
    { id: 15, title: 'Media', category: 'Posters, Bouchers, Visiting Card', route: '/media', color: '#ef4444', hint: '📰 Click for media wizardry!' },
    { id: 16, title: 'Presentation Design', category: 'Slides, Decks & Pitch Presentations', route: '/ppt', color: '#6366f1', hint: '📊 Click to explore presentation designs!' },
  ];

  const handleNavigation = (route) => {
    window.scrollTo(0, 0);
    window.location.href = route;
  };

  const handleDownloadCalendar = () => {
    // Create a simple calendar PDF download
    const link = document.createElement('a');
    link.href = '/calendar-2026.pdf'; // You would need to add this PDF to public folder
    link.download = 'Calendar-2026-Design.pdf';
    link.click();
  };

  return (
    <div className={styles.wrapper}>
      {/* Animated Background Elements */}
      <div className={styles.bgAnimations}>
        <div className={`${styles.bgOrb} ${styles.bgOrb1}`}></div>
        <div className={`${styles.bgOrb} ${styles.bgOrb2}`}></div>
        <div className={`${styles.bgOrb} ${styles.bgOrb3}`}></div>
      </div>

      {/* Cursor Glow Effect */}
      <div 
        className={styles.cursorGlow}
        style={{ 
          left: `${mousePos.x - 128}px`, 
          top: `${mousePos.y - 128}px`,
        }}
      ></div>

      {/* About Section */}
      <section id="about" className={styles.hero}>
        <div className={styles.badge}>
          <Sparkles className={styles.badgeIcon} />
          <span className={styles.pill}
          >Creative Excellence</span>
        </div>
        <h1 className={styles.heroTitle}>
          <span className={styles.gradientText}>About My Design Journey</span>
        </h1>
        <p className={styles.experienceText}>
          Where imagination meets design perfection & Years of creative exploration and artistic growth
        </p>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className={styles.portfolioSection}>
        <div className={styles.container}>
          
          <div className={styles.buttonGrid}>
            {portfolio.map((item) => (
              <div key={item.id} className={styles.buttonWrapper}>
                <button
                  onClick={() => handleNavigation(item.route)}
                  className={styles.portfolioButton}
                  style={{
                    background: `linear-gradient(135deg, ${item.color}22, ${item.color}11)`,
                    borderColor: `${item.color}44`,
                  }}
                  onMouseEnter={(e) => {
                    setHoveredButton(item.id);
                    e.currentTarget.style.transform = 'translateY(-4px) scale(1.05)';
                    e.currentTarget.style.borderColor = item.color;
                    e.currentTarget.style.boxShadow = `0 10px 30px ${item.color}44`;
                  }}
                  onMouseLeave={(e) => {
                    setHoveredButton(null);
                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                    e.currentTarget.style.borderColor = `${item.color}44`;
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div className={styles.buttonTextContainer}>
                    <span className={styles.buttonTitle}>{item.title}</span>
                    <span className={styles.buttonCategory}>{item.category}</span>
                  </div>
                </button>
                {hoveredButton === item.id && (
                  <div className={styles.hintBubble}>
                    <span className={styles.hintEmoji}>🪄</span>
                    <span className={styles.hintText}>{item.hint}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Calendar Section */}
      <Calendar2026 />

      {/* Skills Section */}
      <GraphicDesignCreativeSkills />

      {/* Contact and Footer */}
      <GraphicDesignContact onModeChange={onModeChange} />
    </div>
  );
};

export default GraphicDesignPortfolio;